import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { AuthModal } from '../../components/auth/AuthModal';
import { MessageBubble } from '../../components/chat/MessageBubble';
import { PersonaDropdown } from '../../components/chat/PersonaDropdown';
import { Sidebar } from '../../components/sidebar/Sidebar';
import { useAuthState } from '../../hooks/useAuthState';
import { useChatWorkspace } from '../../hooks/useChatWorkspace';
import { useGuestId } from '../../hooks/useGuestId';
import { useResponsiveSidebar } from '../../hooks/useResponsiveSidebar';

type IconType = 'Attachment' | 'Language' | 'Knowledge';

function IconCircle({
  label,
  light,
  onClick,
}: {
  label: IconType;
  light: boolean;
  onClick: () => void;
}) {
  let iconPath: ReactElement | null = null;
  switch (label) {
    case 'Attachment':
      iconPath = (
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6.75v7.5a4.5 4.5 0 11-9 0v-8.25a3 3 0 016 0v7.5a1.5 1.5 0 11-3 0V7.5" />
      );
      break;
    case 'Language':
      iconPath = (
        <>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m3 6c0 2.5-3 5-3 5s-3-2.5-3-5a3 3 0 116 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11l3 6 3-6m-4 2h2" />
        </>
      );
      break;
    default:
      iconPath = <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-6-6h12" />;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`flex h-8 w-8 items-center justify-center rounded-full border text-slate-500 transition ${
        light
          ? 'border-slate-200 hover:border-slate-400 hover:text-slate-700'
          : 'border-slate-700 hover:border-sky-500 hover:text-slate-100'
      }`}
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        {iconPath}
      </svg>
    </button>
  );
}

export function ChatPage() {
  const guestId = useGuestId();
  const { isDesktop, sidebarOpen, setSidebarOpen } = useResponsiveSidebar();
  const {
    token,
    user,
    authMode,
    setAuthMode,
    authLoading,
    authError,
    authModalOpen,
    openAuthModal,
    closeAuthModal,
    handleAuthSubmit,
    handleLogout,
  } = useAuthState();
  const isAuthenticated = Boolean(token);

  const {
    chats,
    messages,
    selectedChatId,
    activeChat,
    activeAgentId,
    loading,
    loadingMessages,
    initializing,
    error,
    handleSelectChat,
    handleSendMessage,
    handleNewChat,
    handlePersonaSelection,
    resetWorkspace,
    forceLogout,
    acknowledgeForceLogout,
  } = useChatWorkspace({ token, guestId });

  const messageEndRef = useRef<HTMLDivElement | null>(null);
  const [draftMessage, setDraftMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [themeMode, setThemeMode] = useState<'dark' | 'light'>('dark');
  const isLightTheme = themeMode === 'light';
  const toggleThemeMode = useCallback(() => {
    setThemeMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);
  const hasMessages = messages.length > 0;
  const canSend = draftMessage.trim().length > 0 && !loading;

  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (forceLogout) {
      handleLogout();
      resetWorkspace();
      acknowledgeForceLogout();
    }
  }, [forceLogout, handleLogout, resetWorkspace, acknowledgeForceLogout]);

  const handleSignOut = useCallback(() => {
    handleLogout();
    resetWorkspace();
  }, [handleLogout, resetWorkspace]);

  const startNewChat = useCallback(() => {
    handleNewChat();
    setDraftMessage('');
  }, [handleNewChat]);

  const openLoginModal = useCallback(() => {
    setAuthMode('login');
    openAuthModal();
  }, [openAuthModal, setAuthMode]);

  const openRegisterModal = useCallback(() => {
    setAuthMode('register');
    openAuthModal();
  }, [openAuthModal, setAuthMode]);

  const handleAttachmentClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleAttachmentPicked = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      setDraftMessage((prev) => `${prev}${prev ? '\n' : ''}[Attachment: ${file.name}] `);
      event.target.value = '';
    },
    []
  );

  const handleLanguageShortcut = useCallback(() => {
    setDraftMessage((prev) => `${prev}${prev ? '\n' : ''}Please respond in English.`);
  }, []);

  const handleKnowledgeShortcut = useCallback(() => {
    setDraftMessage((prev) => `${prev}${prev ? '\n' : ''}Use my knowledge base context for this reply.`);
  }, []);

  const submitMessage = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSendMessage(draftMessage).then((response) => {
        if (response) {
          setDraftMessage('');
        }
      });
    },
    [draftMessage, handleSendMessage]
  );

  if (!isAuthenticated) {
    const landingBg = isLightTheme ? 'bg-white text-slate-900' : 'bg-slate-950 text-slate-100';
    const heroCardClasses = isLightTheme
      ? 'bg-white text-slate-900 border border-slate-200'
      : 'bg-slate-900/70 text-slate-100 border border-slate-900';
    const inputClasses = isLightTheme
      ? 'bg-white border border-slate-200 text-slate-900'
      : 'bg-slate-950 border border-slate-800 text-slate-100';
    const loginButtonClasses = isLightTheme
      ? 'rounded-full bg-black px-6 py-2 text-sm font-semibold text-white transition hover:bg-slate-800'
      : 'rounded-2xl bg-sky-600 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-sky-500';
    const signupButtonClasses = isLightTheme
      ? 'rounded-full border border-slate-300 px-6 py-2 text-sm font-semibold text-slate-900 transition hover:border-slate-500'
      : 'rounded-2xl border border-slate-700 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-slate-100 transition hover:border-sky-500 hover:text-white';

    return (
      <div className={`relative flex min-h-screen w-full flex-col ${landingBg} transition`}>
        <header className="flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${
                isLightTheme ? 'bg-black text-white' : 'bg-sky-600 text-white'
              }`}
            >
              N
            </div>
            <div>
              <p className={`text-lg font-semibold ${isLightTheme ? 'text-slate-900' : 'text-white'}`}>Niro Chat</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleThemeMode}
              className={`rounded-full border px-5 py-2 text-sm font-medium transition ${
                isLightTheme
                  ? 'border-slate-300 text-slate-700 hover:border-slate-500'
                  : 'border-slate-700 text-slate-100 hover:border-sky-500'
              }`}
            >
              {isLightTheme ? 'Dark mode' : 'Light mode'}
            </button>
            <button type="button" onClick={openLoginModal} className={loginButtonClasses}>
              Log in
            </button>
            <button type="button" onClick={openRegisterModal} className={signupButtonClasses}>
              Sign up
            </button>
          </div>
        </header>

        <main className="flex flex-1 items-center justify-center px-4 pb-16 pt-10 sm:px-6">
          <div
            className={`w-full max-w-3xl rounded-[2.5rem] p-10 text-center shadow-2xl sm:p-16 ${heroCardClasses}`}
          >
            <h1 className={`text-4xl font-semibold ${isLightTheme ? 'text-slate-900' : 'text-white'}`}>
              How can I help today?
            </h1>

            <div className="mt-10">
              <form
                onSubmit={submitMessage}
                className={`flex flex-col gap-3 rounded-[1.5rem] px-5 py-4 text-left shadow-xl sm:flex-row sm:items-center ${inputClasses}`}
              >
                <textarea
                  rows={2}
                  value={draftMessage}
                  onChange={(event) => setDraftMessage(event.target.value)}
                  placeholder="Ask a question or describe a task..."
                  className={`flex-1 resize-none bg-transparent text-base leading-relaxed outline-none ${
                    isLightTheme ? 'text-slate-900 placeholder:text-slate-400' : 'text-slate-100 placeholder:text-slate-500'
                  }`}
                />
                <div className="flex items-center gap-2 text-slate-400">
                  <IconCircle label="Attachment" light={isLightTheme} onClick={handleAttachmentClick} />
                  <IconCircle label="Language" light={isLightTheme} onClick={handleLanguageShortcut} />
                  <IconCircle label="Knowledge" light={isLightTheme} onClick={handleKnowledgeShortcut} />
                </div>
                <button
                  type="submit"
                  disabled={!canSend}
                  className={`flex h-10 w-10 items-center justify-center rounded-full transition ${
                    isLightTheme
                      ? 'bg-black text-white hover:bg-slate-800 disabled:bg-slate-300 disabled:text-slate-500'
                      : 'bg-sky-600 text-white hover:bg-sky-500 disabled:bg-slate-700'
                  }`}
                >
                  {loading ? (
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v4m0 6v4m7-7h-4M9 12H5m9-7l-2 2m0 10-2 2m8-8l2-2m-12 0L5 7" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              </form>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleAttachmentPicked} />
              {error && (
                <div
                  className={`mt-4 rounded-xl border px-4 py-2 text-sm ${
                    isLightTheme ? 'border-rose-200 bg-rose-100 text-rose-800' : 'border-rose-900 bg-rose-950/70 text-rose-200'
                  }`}
                >
                  {error}
                </div>
              )}
            </div>

          </div>
        </main>

        <footer className="px-6 pb-6">
          <p className={`text-xs text-center ${isLightTheme ? 'text-slate-400' : 'text-slate-500'}`}>
            By messaging Niro Chat you agree to our{' '}
            <button type="button" className="underline decoration-slate-300 underline-offset-4">
              Terms
            </button>{' '}
            and{' '}
            <button type="button" className="underline decoration-slate-300 underline-offset-4">
              Privacy Policy
            </button>
            .
          </p>
        </footer>

        {authModalOpen && (
          <AuthModal
            mode={authMode}
            onModeChange={setAuthMode}
            onSubmit={handleAuthSubmit}
            loading={authLoading}
            error={authError}
            onClose={closeAuthModal}
          />
        )}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen w-full bg-slate-950 text-slate-100 lg:h-screen">
      <Sidebar
        open={sidebarOpen}
        chats={chats}
        onSelectChat={handleSelectChat}
        onNewChat={startNewChat}
        onLogout={handleSignOut}
        selectedChatId={selectedChatId}
        showLogout={Boolean(token)}
        isDesktop={isDesktop}
        onClose={() => setSidebarOpen(false)}
        onOpen={() => setSidebarOpen(true)}
      />

      <main className="flex flex-1 flex-col">
        <div className="flex flex-col gap-4 px-4 pt-4 pb-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:pt-6 sm:pb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
            {activeChat && (
              <p className="text-sm text-slate-400 text-center sm:text-left">
                {activeChat.title || 'Untitled chat'}
              </p>
            )}
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            <PersonaDropdown selectedId={activeAgentId} onSelect={handlePersonaSelection} />
            {token && !user && (
              <div className="text-xs uppercase tracking-wide text-slate-500">Syncing profile...</div>
            )}
            {token && user && (
              <div className="flex w-full items-center justify-center gap-2 rounded-full border border-slate-800 bg-slate-900 px-4 py-2 text-xs uppercase tracking-wide text-slate-400 sm:w-auto sm:justify-start">
                <span>{user.full_name || user.email}</span>
              </div>
            )}
            {!token && (
              <button
                type="button"
                onClick={openLoginModal}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-slate-800 bg-slate-900/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-200 transition hover:border-sky-500 hover:text-slate-100 sm:w-auto sm:justify-start"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 20v-1a6 6 0 0112 0v1" />
                </svg>
                <span>Sign in</span>
              </button>
            )}
          </div>
        </div>

        <div className="relative flex flex-1 flex-col overflow-hidden">
          {initializing || loadingMessages ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-6 py-4 text-sm text-slate-300">
                Loading your workspace...
              </div>
            </div>
          ) : !hasMessages ? (
            <div className="flex flex-1 flex-col items-center justify-center px-4 sm:px-6">
              <div className="w-full max-w-3xl rounded-3xl border border-slate-900 bg-slate-900/70 p-8 text-center shadow-lg sm:max-w-4xl sm:p-12 lg:max-w-6xl">
                <h2 className="text-3xl font-semibold text-slate-100">How can I help today?</h2>
                <p className="mt-3 text-sm text-slate-400">
                  Choose an agent, paste a question, and I will combine your knowledge base with powerful reasoning.
                </p>

                <form className="mt-8 space-y-4" onSubmit={submitMessage}>
                  <textarea
                    rows={4}
                    value={draftMessage}
                    onChange={(event) => setDraftMessage(event.target.value)}
                    placeholder="Ask a question or describe a task..."
                    className="w-full rounded-3xl border border-slate-800 bg-slate-950 px-5 py-4 text-base text-slate-100 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-500/40 sm:px-6 sm:py-5"
                  />
                  {error && (
                    <div className="rounded-xl border border-rose-900 bg-rose-950/70 px-4 py-3 text-sm text-rose-200">
                      {error}
                    </div>
                  )}
                  <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
                    <button
                      type="submit"
                      disabled={!canSend}
                      className="w-full rounded-2xl bg-sky-600 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:bg-slate-700 sm:w-auto"
                    >
                      {loading ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-4 pb-6 sm:px-6">
                <div className="mx-auto flex w-full max-w-3xl flex-col pt-2 sm:max-w-4xl lg:max-w-6xl">
                  {messages.map((message) => (
                    <MessageBubble key={message.id} message={message} />
                  ))}
                  {loading && (
                    <div className="mb-6 flex justify-start">
                      <div className="rounded-2xl bg-slate-900 px-5 py-3 text-sm text-slate-300 shadow-sm">
                        Thinking...
                      </div>
                    </div>
                  )}
                  <div ref={messageEndRef} />
                </div>
              </div>

              <form onSubmit={submitMessage} className="border-t border-slate-900 bg-slate-950/70 px-4 py-4 sm:px-6 sm:py-6">
                <div className="mx-auto flex w-full max-w-3xl flex-col gap-3 sm:max-w-4xl lg:max-w-6xl">
                  <textarea
                    rows={3}
                    value={draftMessage}
                    onChange={(event) => setDraftMessage(event.target.value)}
                    placeholder="Ask a follow-up or start a new topic..."
                    className="w-full rounded-2xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-500/40 sm:px-5 sm:py-4"
                  />
                  {error && (
                    <div className="rounded-xl border border-rose-900 bg-rose-950/70 px-4 py-3 text-sm text-rose-200">
                      {error}
                    </div>
                  )}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                    <button
                      type="submit"
                      disabled={!canSend}
                      className="w-full rounded-2xl bg-sky-600 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:bg-slate-700 sm:w-auto"
                    >
                      {loading ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </main>

      {authModalOpen && (
        <AuthModal
          mode={authMode}
          onModeChange={setAuthMode}
          onSubmit={handleAuthSubmit}
          loading={authLoading}
          error={authError}
          onClose={closeAuthModal}
        />
      )}
    </div>
  );
}
